import type { CurrencyCode } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import {
  updateLocationGeneralPreferencesMutationAtom,
  UpdateLocationGeneralPreferencesInput,
} from '../_graphql/mutationUpdateLocationGeneralPreferences';
import {
  locationGeneralPreferencesQueryAtom,
  UnitSystem,
} from '../_graphql/queryLocationGeneralPreferences';

import { $facilityId } from './locations.state';

const $savedLocationGeneralPreferences = locationGeneralPreferencesQueryAtom(
  (get) => {
    const facilityId = get($facilityId);
    return {
      variables: { facilityId: facilityId ?? '' },
      skip: !facilityId,
    };
  },
);

export interface LocationGeneralPreferencesFormState {
  dateFormat: string;
  weekStartDay: string;
  language: string;
  timezone: string;
  currency: string;
  allowedUnitSystems: UnitSystem[];
  defaultUnitSystem: UnitSystem;
}

const defaultFormState: LocationGeneralPreferencesFormState = {
  dateFormat: 'MM/DD/YYYY',
  weekStartDay: 'Monday',
  language: 'en',
  timezone: 'America/New_York',
  currency: 'USD',
  allowedUnitSystems: ['Metric', 'Imperial'],
  defaultUnitSystem: 'Metric',
};

export const $locationGeneralPreferencesLocalChanges = atom<
  Partial<LocationGeneralPreferencesFormState>
>({});

export const $displayedLocationGeneralPreferences = atom((get) => {
  const queryData = get($savedLocationGeneralPreferences).data;
  const localChanges = get($locationGeneralPreferencesLocalChanges);

  const saved: LocationGeneralPreferencesFormState = queryData
    ? {
        dateFormat: queryData.dateFormat,
        weekStartDay: queryData.weekStartDay,
        language: queryData.language,
        timezone: queryData.timezone,
        currency: queryData.currency,
        allowedUnitSystems: [...queryData.allowedUnitSystems],
        defaultUnitSystem: queryData.defaultUnitSystem,
      }
    : defaultFormState;

  return { ...saved, ...localChanges };
});

export const $updateLocationGeneralPreferencesForm = atom(
  null,
  (get, set, update: Partial<LocationGeneralPreferencesFormState>) => {
    const currentChanges = get($locationGeneralPreferencesLocalChanges);
    set($locationGeneralPreferencesLocalChanges, {
      ...currentChanges,
      ...update,
    });
  },
);

export const $toggleUnitSystem = atom(
  null,
  (get, set, params: { unitSystem: UnitSystem; enabled: boolean }) => {
    const currentState = get($displayedLocationGeneralPreferences);
    const { unitSystem, enabled } = params;

    let newAllowedSystems = [...currentState.allowedUnitSystems];

    if (enabled && !newAllowedSystems.includes(unitSystem)) {
      newAllowedSystems.push(unitSystem);
    } else if (!enabled) {
      if (
        newAllowedSystems.length > 1 &&
        currentState.defaultUnitSystem !== unitSystem
      ) {
        newAllowedSystems = newAllowedSystems.filter((s) => s !== unitSystem);
      }
    }

    const currentChanges = get($locationGeneralPreferencesLocalChanges);
    set($locationGeneralPreferencesLocalChanges, {
      ...currentChanges,
      allowedUnitSystems: newAllowedSystems,
    });
  },
);

export const $setDefaultUnitSystem = atom(
  null,
  (get, set, unitSystem: UnitSystem) => {
    const currentState = get($displayedLocationGeneralPreferences);
    const currentChanges = get($locationGeneralPreferencesLocalChanges);

    const newAllowedSystems = [...currentState.allowedUnitSystems];
    if (!newAllowedSystems.includes(unitSystem)) {
      newAllowedSystems.push(unitSystem);
    }

    set($locationGeneralPreferencesLocalChanges, {
      ...currentChanges,
      allowedUnitSystems: newAllowedSystems,
      defaultUnitSystem: unitSystem,
    });
  },
);

export const $hasUnsavedChanges = atom((get) => {
  const localChanges = get($locationGeneralPreferencesLocalChanges);
  return Object.keys(localChanges).length > 0;
});

export const $discardLocationGeneralPreferencesChanges = atom(
  null,
  (_get, set) => {
    set($locationGeneralPreferencesLocalChanges, {});
  },
);

const $updateLocationGeneralPreferencesMutation =
  updateLocationGeneralPreferencesMutationAtom();

export const $saveLocationGeneralPreferences = atom(null, async (get, set) => {
  const facilityId = get($facilityId);
  const form = get($displayedLocationGeneralPreferences);

  if (!facilityId) return;

  const input: UpdateLocationGeneralPreferencesInput = {
    facilityId,
    dateFormat: form.dateFormat,
    weekStartDay: form.weekStartDay,
    language: form.language,
    timezone: form.timezone,
    currency: form.currency as CurrencyCode,
    metricEnabled: form.allowedUnitSystems.includes('Metric'),
    imperialEnabled: form.allowedUnitSystems.includes('Imperial'),
    defaultUnitSystem: form.defaultUnitSystem,
  };

  const mutationResult = await set(
    $updateLocationGeneralPreferencesMutation,
    input,
  );

  const result = mutationResult?.updateFacilityGeneralPreferences;
  if (result) {
    set($locationGeneralPreferencesLocalChanges, {});
  }
});
